/**
 * The HUD strip: RawScore (counting up), level number + theme name, session
 * accuracy ring, a small preview of the next problem, and the pause button.
 */
import { memo, useState } from 'react';
import type { Locale, Problem } from '@dropstack/core';
import { useT } from '../i18n/index.ts';
import { formatInteger, formatPercent } from '../screens/model/format.ts';
import { GameIcon } from './icons.tsx';
import { FittedMath } from './FittedMath.tsx';
import { speechFor } from './mathView.ts';
import { retarget, tickerAt, tickerValue, type ScoreTicker } from './scoreTicker.ts';

/** The score, counting up to each new value (instantly under reduced motion). */
export function useTickingScore(score: number, now: number, reducedMotion: boolean): number {
  const [ticker, setTicker] = useState<ScoreTicker>(() => tickerAt(score, now));
  if (ticker.to !== score) {
    // A new score: start counting from what is on screen (React's "adjust state while rendering" pattern).
    const next = reducedMotion ? tickerAt(score, now) : retarget(ticker, score, now);
    setTicker(next);
    return tickerValue(next, now);
  }
  return tickerValue(ticker, now);
}

export interface AccuracyRingProps {
  /** 0–1, or null before the first answer. */
  accuracy: number | null;
  size?: number;
}

/** A ring that fills with the share of correct answers, the percentage inside. */
export const AccuracyRing = memo(function AccuracyRing({ accuracy, size = 44 }: AccuracyRingProps) {
  const t = useT();
  const r = 16;
  const c = 2 * Math.PI * r;
  const fraction = accuracy ?? 0;
  const label = accuracy === null ? t('game.accuracyNone') : t('game.accuracyLabel', { percent: formatPercent(accuracy) });
  return (
    <span className="hud__ring" role="img" aria-label={label} title={label}>
      <svg width={size} height={size} viewBox="0 0 40 40" aria-hidden="true" focusable="false">
        <circle className="hud__ring-track" cx="20" cy="20" r={r} />
        <circle
          className="hud__ring-fill"
          cx="20"
          cy="20"
          r={r}
          strokeDasharray={`${(fraction * c).toFixed(2)} ${c.toFixed(2)}`}
          transform="rotate(-90 20 20)"
        />
      </svg>
      <span className="hud__ring-text" aria-hidden="true">
        {accuracy === null ? '–' : Math.round(accuracy * 100)}
      </span>
    </span>
  );
});

export interface PauseButtonProps {
  onPause(): void;
  disabled: boolean;
}

export function PauseButton({ onPause, disabled }: PauseButtonProps) {
  const t = useT();
  return (
    <button
      type="button"
      className="hud__pause"
      onClick={onPause}
      disabled={disabled}
      aria-label={t('game.pauseLabel')}
      title={t('game.pauseLabel')}
      aria-keyshortcuts="Escape"
    >
      <GameIcon name="pause" size="1.5em" />
    </button>
  );
}

export interface HudProps {
  score: number;
  level: number;
  themeName: string;
  accuracy: number | null;
  next: Problem;
  locale: Locale;
  onPause(): void;
  canPause: boolean;
}

export const Hud = memo(function Hud({ score, level, themeName, accuracy, next, locale, onPause, canPause }: HudProps) {
  const t = useT();
  const nextSpeech = speechFor(next.prompt, locale);
  return (
    <header className="hud">
      <div className="hud__score" role="group" aria-label={t('game.scoreLabel', { score: formatInteger(score) })}>
        <span className="hud__label" aria-hidden="true">
          {t('game.score')}
        </span>
        <span className="hud__score-value" aria-hidden="true">
          {formatInteger(score)}
        </span>
      </div>
      <div className="hud__level" aria-label={t('game.levelTheme', { level, theme: themeName })} role="group">
        <span className="hud__level-number" aria-hidden="true">
          {t('common.levelN', { level })}
        </span>
        <span className="hud__theme" aria-hidden="true">
          {themeName}
        </span>
      </div>
      <AccuracyRing accuracy={accuracy} />
      <div className="hud__next" role="group" aria-label={t('game.nextLabel', { sum: nextSpeech })}>
        <span className="hud__label" aria-hidden="true">
          {t('game.next')}
        </span>
        <FittedMath markup={next.prompt} locale={locale} basePx={18} minPx={11} silent className="hud__next-sum" />
      </div>
      <PauseButton onPause={onPause} disabled={!canPause} />
    </header>
  );
});
